if (!customElements.get("media-with-content-animation")) {
  class MediaWithContentAnimation extends HTMLElement {
    constructor() {
      super();
      this._rafId = null;
      this._lastProgress = -1;
    }

    connectedCallback() {
      this.section =
          this.querySelector(".media-with-content") ||
          this.closest(".media-with-content");

      if (!this.section) return;

      this.media = this.querySelector(
          ".media-with-content__media-blur-wrapper, .media-block",
      );
      this.content = this.querySelector(
          ".media-with-content__content-blur-wrapper, .media-with-content__content",
      );

      if (!this.media || !this.content) return;

      this.resizeHandler = this.onResize.bind(this);
      window.addEventListener("resize", this.resizeHandler);

      setTimeout(() => {
        this.init();
        this._startRafLoop();
      }, 100);
    }

    disconnectedCallback() {
      window.removeEventListener("resize", this.resizeHandler);
      if (this._rafId) cancelAnimationFrame(this._rafId);
    }

    _startRafLoop() {
      const loop = () => {
        this.updatePosition();
        this._rafId = requestAnimationFrame(loop);
      };
      this._rafId = requestAnimationFrame(loop);
    }

    onResize() {
      clearTimeout(this.initTimeout);
      this.initTimeout = setTimeout(() => this.init(), 100);
    }

    getZoom() {
      const raw = getComputedStyle(this.section).zoom;
      if (!raw || raw === 'normal') return 1;
      const n = parseFloat(raw);
      if (isNaN(n)) return 1;
      return n > 1 ? n / 100 : n;
    }

    isMobile() {
      return window.innerWidth <= 749;
    }

    init() {
      this.media.style.transform = '';
      this.content.style.transform = '';
      void this.section.offsetHeight;

      if (this.isMobile()) {
        this.mediaDelta = null;
        this.contentDelta = null;
        return;
      }

      const zoom = this.getZoom();
      this._zoom = zoom;

      // Detect if browser returns pre-zoom rects (Safari) or post-zoom rects (Chrome)
      const isPreZoom = Math.abs(this.section.getBoundingClientRect().width - this.section.offsetWidth) < 2;
      this._isPreZoom = isPreZoom;

      const sectionRect = this.section.getBoundingClientRect();
      const mediaRect = this.media.getBoundingClientRect();
      const contentRect = this.content.getBoundingClientRect();

      const sectionCenter = {
        x: sectionRect.width / 2,
        y: sectionRect.height / 2,
      };

      const rawMediaDelta = {
        x: sectionCenter.x - (mediaRect.left - sectionRect.left + mediaRect.width / 2),
        y: sectionCenter.y - (mediaRect.top - sectionRect.top + mediaRect.height / 2),
      };

      const rawContentDelta = {
        x: sectionCenter.x - (contentRect.left - sectionRect.left + contentRect.width / 2),
        y: sectionCenter.y - (contentRect.top - sectionRect.top + contentRect.height / 2),
      };

      // translate() operates in pre-zoom space.
      // Safari rects are pre-zoom → delta is already correct for translate(), no adjustment needed.
      // Chrome rects are post-zoom → divide by zoom to convert to pre-zoom space for translate().
      const deltaScale = isPreZoom ? 1 : zoom;

      this.mediaDelta = {
        x: rawMediaDelta.x / deltaScale,
        y: rawMediaDelta.y / deltaScale,
      };

      this.contentDelta = {
        x: rawContentDelta.x / deltaScale,
        y: rawContentDelta.y / deltaScale,
      };

      this._lastProgress = -1;
      this.updatePosition();
    }

    updatePosition() {
      if (!this.section || !this.mediaDelta) return;
      if (this.isMobile()) return;

      const rect = this.section.getBoundingClientRect();
      const vh = window.innerHeight;
      const zoom = this._zoom;

      // Formula A: accounts for pre-zoom rect coordinates on Safari.
      // On Safari rect.top and rect.height are pre-zoom → multiply by zoom for visual space.
      // On Chrome they are already post-zoom → multiplying by zoom is also correct
      // since Chrome's zoom === 1 effectively (rects already in visual space) ...
      // Wait — Chrome also has zoom 0.75 but returns post-zoom rects,
      // so Chrome rect.height is already visual. Multiplying by zoom would be wrong for Chrome.
      // Solution: use isPreZoom flag.
      const top    = this._isPreZoom ? rect.top    * zoom : rect.top;
      const height = this._isPreZoom ? rect.height * zoom : rect.height;

      // let progress = (vh - top) / height;
      let progress = (vh - top) / (height * 1.5);
      //todo when transition starts (increase the value to start before the animation)
      if (this._isPreZoom) progress += 0.4;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      if (Math.abs(progress - this._lastProgress) < 0.0001) return;
      this._lastProgress = progress;

      const factor = 1 - progress;
      this.media.style.transform = `translate(${this.mediaDelta.x * factor}px, ${this.mediaDelta.y * factor}px)`;
      this.content.style.transform = `translate(${this.contentDelta.x * factor}px, ${this.contentDelta.y * factor}px)`;
    }
  }

  customElements.define(
      "media-with-content-animation",
      MediaWithContentAnimation,
  );
}